import { useState } from "react";
import { Star } from "lucide-react";

interface ReviewFormProps {
    onSubmit: ({ comment, rating }: { comment: string; rating: number }) => void;
}

export default function ReviewForm({ onSubmit }: ReviewFormProps) {
    const [rating, setRating] = useState<number>(0);
    const [hoveredRating, setHoveredRating] = useState<number>(0);
    const [comment, setComment] = useState<string>("");
    const [error, setError] = useState<string>("");

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        // Validation Marketplace standard
        if (rating === 0) {
            setError("Veuillez sélectionner une note en étoiles.");
            return;
        }

        if (!comment.trim()) {
            setError("Veuillez rédiger un commentaire.");
            return;
        }

        onSubmit({ comment: comment.trim(), rating });
        
        // Reset après soumission réussie
        setComment("");
        setRating(0);
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-4 rounded-xl border border-gray-200 max-w-xl">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Donner votre avis</h3>

            {/* Sélection de la Note (Étoiles) */}
            <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Votre note *
                </label>
                <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHoveredRating(star)}
                            onMouseLeave={() => setHoveredRating(0)}
                            className="p-1 focus:outline-none transition-transform active:scale-95"
                        >
                            <Star
                                className={`w-6 h-6 transition-colors ${
                                    star <= (hoveredRating || rating)
                                        ? "fill-amber-400 text-amber-400"
                                        : "text-gray-300"
                                }`}
                            />
                        </button>
                    ))}
                </div>
            </div>

            {/* Zone de Commentaire */}
            <div className="mb-4">
                <label htmlFor="comment" className="block text-xs font-semibold text-gray-600 mb-1.5">
                    Votre commentaire *
                </label>
                <textarea
                    id="comment"
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Qu'avez-vous pensé de votre expérience ?"
                    className="w-full text-sm p-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-gray-50 outline-none resize-none"
                />
            </div>

            {/* Message d'erreur s'il y a lieu */}
            {error && (
                <p className="text-xs font-medium text-red-600 mb-3">{error}</p>
            )}

            {/* Bouton d'action direct */}
            <button
                type="submit"
                className="w-full sm:w-auto px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg shadow-sm transition"
            >
                Soumettre l'avis
            </button>
        </form>
    );
}